import { AnimatePresence } from "framer-motion";
import { Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageBubble, TypingIndicator } from "@/components/message-bubble";
import type { ChatMessage } from "@/types";

export function ChatWindow({
  messages,
  onSend,
  isLoading = false,
  isAwaiting,
}: {
  messages: ChatMessage[];
  onSend?: (text: string) => void;
  isLoading?: boolean;
  /**
   * When provided, the parent controls whether we are awaiting a response.
   * Used by chat views that share one ChatWindow across multiple sessions —
   * if the parent says "this session isn't the one with the pending request"
   * we don't show a stale typing indicator.
   */
  isAwaiting?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [internalAwaiting, setInternalAwaiting] = useState(false);
  const isControlled = isAwaiting !== undefined;
  const awaiting = isControlled ? isAwaiting! : internalAwaiting;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, awaiting, isLoading, scrollToBottom]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !onSend || awaiting || isLoading) return;
    setDraft("");
    if (!isControlled) setInternalAwaiting(true);
    try {
      await onSend(text);
    } finally {
      if (!isControlled) setInternalAwaiting(false);
    }
  }

  const showInput = Boolean(onSend);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        data-allow-scroll
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-5"
      >
        <AnimatePresence initial={false}>
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {awaiting && <TypingIndicator key="typing" />}
        </AnimatePresence>
      </div>

      {showInput && (
        <div className="shrink-0 border-t border-slate-200 bg-white p-3 sm:p-4">
          <form
            className="flex items-end gap-2 rounded-[1.6rem] border border-slate-200 bg-slate-50 p-2"
            onSubmit={handleSubmit}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Ask anything about your results..."
              rows={1}
              className="min-h-[44px] flex-1 resize-none bg-transparent px-3 py-2.5 text-[16px] leading-6 text-slate-800 outline-none placeholder:text-slate-400"
            />
            <button
              type="submit"
              disabled={!draft.trim() || awaiting || isLoading}
              className="grid size-11 shrink-0 place-items-center rounded-2xl bg-navy text-white transition hover:bg-navy-hover disabled:cursor-not-allowed disabled:bg-slate-300"
              aria-label="Send message"
            >
              <Send className="size-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
